import axios from 'axios';
import valid from 'card-validator';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import Loader from 'react-loader-spinner';
import { useLocation } from 'react-router';

const baseUrl = process.env.REACT_APP_BASE_URL;
const stripeKey = process.env.REACT_APP_STRIPE_KEY;
const stripeUrl = process.env.REACT_APP_STRIPE_URL;

const Donate = () => {
  const search = useLocation().search;
  const sponsoredKidFirstName = new URLSearchParams(search).get('firstName');
  const sponsoredKidLastName = new URLSearchParams(search).get('lastName');
  const sponsoredKid = new URLSearchParams(search).get('code');

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({
    amount: '',
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    message: '',
    cardHolderName: '',
    cardNumber: '',
    cardCvc: '',
    cardExpiryDate: '',
    error: {
      cardHolderName: '',
      cardNumber: '',
      cardCvc: '',
      cardExpiryDate: '',
    },
  });

  const handleInputChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    setPayload({ ...payload, [key]: value });
  };

  const validateCardDetails = () => {
    let isValid = true;
    const cvcValidation = valid.cvv(payload.cardCvc);
    const numberValidation = valid.number(payload.cardNumber);
    const nameValidation = valid.cardholderName(payload.cardHolderName);
    const expiryValidation = valid.expirationDate(payload.cardExpiryDate);

    if (!nameValidation.isPotentiallyValid) {
      setPayload({
        ...payload,
        error: {
          ...payload.error,
          cardNumber: null,
          cardHolderName: 'Invalid card holder name',
        },
      });

      isValid = false;
      return;
    }

    if (!numberValidation.isPotentiallyValid) {
      setPayload({
        ...payload,
        error: {
          ...payload.error,
          cardHolderName: null,
          cardNumber: 'Invalid card number',
        },
      });

      isValid = false;
      return;
    }

    if (!expiryValidation.isPotentiallyValid) {
      setPayload({
        ...payload,
        error: {
          ...payload.error,
          cardHolderName: null,
          cardNumber: null,
          cardExpiryDate: 'Invalid card expiry date',
        },
      });

      isValid = false;
      return;
    }

    if (!cvcValidation.isPotentiallyValid) {
      setPayload({
        ...payload,
        error: {
          ...payload.error,
          cardExpiryDate: null,
          cardHolderName: null,
          cardNumber: null,
          cardCvc: 'Invalid cvc',
        },
      });

      isValid = false;
      return;
    }

    if (isValid) {
      setPayload({
        ...payload,
        error: {
          ...payload.error,
          cardExpiryDate: null,
          cardHolderName: null,
          cardNumber: null,
          cardCvc: null,
        },
      });
    }

    return isValid;
  };

  const fetchCardToken = (card) => {
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${stripeKey}`,
      },
    };

    const params = new URLSearchParams();

    params.append('card[number]', card.number);
    params.append('card[exp_month', card.exp_month);
    params.append('card[exp_year]', card.exp_year);
    params.append('card[cvc]', card.cvc);
    params.append('card[exp_year]', card.exp_year);
    params.append('card[name]', card.name);
    params.append('card[address_line1]', card.address_line1);

    return new Promise((resolve, reject) => {
      axios
        .post(stripeUrl, params, config)
        .then(({ data }) => {
          return resolve(data);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const isCardValid = validateCardDetails();

    if (!isCardValid) return;

    setLoading(true);

    const {
      firstName,
      lastName,
      cardCvc,
      cardExpiryDate,
      cardNumber,
      address,
      message,
      email,
      amount,
    } = payload;

    try {
      let res;
      const token = await fetchCardToken({
        number: cardNumber,
        exp_month: cardExpiryDate.includes('/')
          ? cardExpiryDate.split('/')[0]
          : cardExpiryDate.slice(0, 2),
        exp_year: cardExpiryDate.includes('/')
          ? cardExpiryDate.split('/')[1]
          : cardExpiryDate.slice(2, 4),
        cvc: cardCvc,
        name: firstName + '' + lastName,
        address_line1: address,
      });

      const data = {
        firstName,
        lastName,
        address,
        message,
        email,
        amount,
        stripeToken: token.id,
      };

      if (sponsoredKid) {
        data.sponsoredKid = sponsoredKid;
        res = await axios.post(`${baseUrl}/sponsored-kid-donation`, data);

        setLoading(false);
        toast.success(
          `Donation paid. Thank you for sponsoring ${sponsoredKidFirstName} ${sponsoredKidLastName}!`
        );
      } else {
        res = await axios.post(`${baseUrl}/donation`, data);
        setLoading(false);
        toast.success(res.data.message);
      }

      setPayload({
        amount: '',
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        message: '',
        cardHolderName: '',
        cardNumber: '',
        cardCvc: '',
        cardExpiryDate: '',
        error: {
          cardHolderName: '',
          cardNumber: '',
          cardCvc: '',
          cardExpiryDate: '',
        },
      });

      setTimeout(() => {
        window.location.replace(res.data.data.stripeReceiptUrl);
      }, 6000);
    } catch (error) {
      setLoading(false);

      if (error.response.data.error.message)
        return toast.error(`${error.response.data.error.message}`);

      if (error.response.data.error)
        return toast.error(
          `Could not process the payment - ${error.response.data.error}`
        );

      toast.error('Could not process the payment - try again');
    }
  };

  return (
    <div className="wpo-donation-page-area section-padding">
      <div className="container">
        <div className="row">
          <div className="col-lg-8 offset-lg-2">
            <div className="wpo-donate-header">
              <h2> THE POWER OF GIVING </h2>
            </div>
            <p>
              Giving is the foundation of change. A small act of kindness can go
              a long way. It can change another person’s life. You can give
              love, hope, care, time, and your resources.
            </p>
            <p className="pb-5">
              Our goals and projects will not be achieved without your financial
              support. It takes just one person to make a difference in child’s
              life. All donations will go towards, children’s education,
              healthcare, children’s talent development, small business creation
              for self-sufficiency in vulnerable families, and children’s
              events. I encourage you to come and support our goal of eliminate
              child street and homelessness by donating to Love Hope Foundation
              (Business #76459 6730 BC0001) and Tax-deductible receipt are in
              process. Thank you for investing in the lives of others!
            </p>
            <div className="wpo-donate-header">
              <h2>Make a Donation</h2>
            </div>
            <form onSubmit={submitHandler} action="#">
              <div className="wpo-donations-amount">
                {sponsoredKidFirstName && sponsoredKidLastName ? (
                  <h2>
                    Your Donation for {sponsoredKidFirstName}{' '}
                    {sponsoredKidLastName}
                  </h2>
                ) : (
                  <h2>Your Donation</h2>
                )}
                <input
                  type="number"
                  className="form-control"
                  name="amount"
                  placeholder="Enter Donation Amount in USD"
                  value={payload.amount}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="wpo-donations-details">
                <h2>Personal Details</h2>
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-6 col-12 form-group">
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      placeholder="First Name"
                      value={payload.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-6 col-12 form-group">
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      placeholder="Last Name"
                      value={payload.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-6 col-12 form-group clearfix">
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="Email"
                      value={payload.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-6 col-12 form-group">
                    <input
                      type="text"
                      className="form-control"
                      name="address"
                      placeholder="Adress"
                      value={payload.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-lg-12 col-12 form-group">
                    <textarea
                      className="form-control"
                      name="message"
                      placeholder="Message"
                      value={payload.message}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>
                </div>
              </div>
              <div className="wpo-donation-payment">
                <h2>Card Details</h2>
                <div className="wpo-payment-area">
                  <div className="row">
                    <div className="col-12">
                      <div className="wpo-payment-option" id="open4">
                        <div id="open5" className="payment-name">
                          <div className="contact-form form-style">
                            <div className="row">
                              <div className="col-lg-6 col-md-12 col-12">
                                <label>Card Holder Name</label>
                                <input
                                  type="text"
                                  placeholder="John Doe"
                                  name="cardHolderName"
                                  value={payload.cardHolderName}
                                  onChange={handleInputChange}
                                  required
                                />
                                <p className="text-danger">
                                  {payload.error.cardHolderName
                                    ? payload.error.cardHolderName
                                    : ''}
                                </p>
                              </div>
                              <div className="col-lg-6 col-md-12 col-12">
                                <label>Card Number</label>
                                <input
                                  type="text"
                                  placeholder="4242 4242 4242 4242"
                                  value={payload.cardNumber}
                                  onChange={handleInputChange}
                                  id="cardNumber"
                                  name="cardNumber"
                                  required
                                />
                                <p className="text-danger">
                                  {payload.error.cardNumber
                                    ? payload.error.cardNumber
                                    : ''}
                                </p>
                              </div>
                              <div className="col-lg-6 col-md-12 col-12">
                                <label>CVC</label>
                                <input
                                  type="text"
                                  placeholder="111"
                                  value={payload.cardCvc}
                                  onChange={handleInputChange}
                                  name="cardCvc"
                                  required
                                />
                                <p className="text-danger">
                                  {payload.error.cardCvc
                                    ? payload.error.cardCvc
                                    : ''}
                                </p>
                              </div>
                              <div className="col-lg-6 col-md-12 col-12">
                                <label>Expiry Date</label>
                                <input
                                  type="text"
                                  placeholder="10/25"
                                  value={payload.cardExpiryDate}
                                  onChange={handleInputChange}
                                  name="cardExpiryDate"
                                  required
                                />
                                <p className="text-danger">
                                  {payload.error.cardExpiryDate
                                    ? payload.error.cardExpiryDate
                                    : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="submit-area">
                {!loading ? (
                  <button
                    type="submit"
                    className="theme-btn submit-btn"
                    disabled
                  >
                    Coming soon
                  </button>
                ) : (
                  <button className="theme-btn submit-btn" disabled>
                    <Loader
                      type="Bars"
                      color="#FFFFFF"
                      height={20}
                      width={30}
                    />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
